import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import * as _ from "lodash";
import * as Papa from "papaparse";
import { FileUpload } from "primeng/fileupload";
import { ToasterService } from "src/app/toaster.service";
import { environment } from "src/environments/environment";
import { SubscriptionService } from "../subscription.service";
import { SharedService } from 'src/app/shared/shared.service';
import { LoaderService } from "src/app/loader.service";
import { AlertDialogOptions, AlertDialogService } from "src/app/shared/alert-dialog/alert-dialog.service";
import { subscriptionCreateSuccessAlert, subscriptionOperationFailureAlert } from '../subscription.create-alert';
import { MatDialog } from '@angular/material';
import * as moment from "moment";
import Utils from "src/app/utils/utils";
import { AuthService } from 'src/app/auth.service';
import { AuthzAccessHintService } from 'src/app/shared/authz-access-hint/authz-access-hint.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { planTypeFromConfig, applicableConfig } from '../subscription.applicable-types';


@Component({
    selector: 'bulk-subscription-create',
    templateUrl: './bulk.subscription.create.component.html',
    styleUrls: ['./bulk.subscription.create.component.css'],
})
export class BulkCreateSubscriptionComponent implements OnInit {

    constructor(
        public toasterService: ToasterService,
        private subscriptionService: SubscriptionService,
        private loaderService: LoaderService,
        private alertDialogService: AlertDialogService,
        private sharedService: SharedService,
        private dialog: MatDialog,
        private authService: AuthService,
        private authzAccessHintService: AuthzAccessHintService,
    ) {
    }
    public authzAccessHint: { message: string; requestUrl: string } | null = null;
    @ViewChild('constructUpload') fileUploadRef: FileUpload;
    @ViewChild("OverlappingPriorityRuleAlert") OverlappingPriorityRuleAlert: TemplateRef<any>;
    fileDetails: any;
    public states: { id: string, name: string, cities: { id: string, name: string }[] }[] = [];
    public cities: { id: string, name: string; }[] = [];
    public modes: { id: string, name: string }[] = [];
    public categories: { id: string, name: string }[] = [];
    public services: {
        id: string,
        name: string,
        serviceDetailId: string,
        categoryId?: string,
        mode?: string,
        active?: boolean
    }[] = [];
    private allServices: {
        id: string,
        name: string,
        serviceDetailId: string,
        categoryId: string,
        active: boolean
    }[] = [];
    private configsBySdid: { [sdid: string]: any } = {};
    private matchedRule: any = null;
    public todayDate: Date = new Date();
    public minDate: Date = this.todayDate;
    public maxDate: Date;
    public overlappingRules: any = [];
    public Utils = Utils;

    subscriptionTypes: string[] = ["REGULAR", "CASHBACK"];
    public hasCashbackPermission: boolean = false;

    subscriptionConstructFormGroup = new FormGroup({
        selectedType: new FormControl("REGULAR", Validators.required),
        selectedState: new FormControl(null),
        selectedCity: new FormControl(null),
        selectedMode: new FormControl(null),
        selectedCategory: new FormControl(null),
        selectedService: new FormControl(null),
        fromDate: new FormControl(null, Validators.required),
        toDate: new FormControl(null, Validators.required),
    })

    formValue(name) {
        return this.subscriptionConstructFormGroup.get(name).value;
    }

    resetOnchangeOf(controlName: string, toReset: string[], cb = () => { }) {
        this.subscriptionConstructFormGroup.get(controlName).valueChanges.subscribe(() => {
          toReset.forEach(control => {
            this.subscriptionConstructFormGroup.get(control).setValue(null);
          });
          cb();
        });
    }

    get taxInfoText() {
        return this.applicableConfig().exclusiveOfTax === true
            ? 'This amount is exclusive of tax'
            : 'This amount is inclusive of tax';
    }

    get isCashback() {
        return this.formValue("selectedType") === "CASHBACK";
    }

    async ngOnInit(): Promise<void> {
        this.resetOnchangeOf("selectedType", ["selectedState", "selectedCity", "selectedMode", "selectedCategory", "selectedService"], () => {
            this.cities = [];
            this.modes = [];
            this.categories = [];
            this.services = [];
            this.allServices = [];
            this.matchedRule = null;
            this.resetFile();
        });
        this.resetOnchangeOf("selectedState", ["selectedCity", "selectedMode", "selectedCategory", "selectedService"], () => {
            const state = this.formValue("selectedState");
            this.cities = state && state.cities ? state.cities : [];
            this.modes = [];
            this.categories = [];
            this.services = [];
            this.allServices = [];
            this.matchedRule = null;
        });
        this.resetOnchangeOf("selectedCity", ["selectedMode", "selectedCategory", "selectedService"], () => {
            this.modes = [];
            this.categories = [];
            this.services = [];
            this.allServices = [];
            this.matchedRule = null;
            this.fetchModes();
        });
        this.resetOnchangeOf("selectedMode", ["selectedCategory", "selectedService"], () => {
            this.categories = [];
            this.services = [];
            this.allServices = [];
            this.matchedRule = null;
            this.fetchCategoriesAndServices();
        });
        this.resetOnchangeOf("selectedCategory", ["selectedService"], () => {
            this.matchedRule = null;
            this.filterPrepaidServices();
        });
        this.resetOnchangeOf("selectedService", [], () => {
            this.matchSelectedService();
        });
        const permissions: string[] = await this.authService.getPermissions();
        this.hasCashbackPermission = permissions.includes('cashback_subscription_create');
        this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('subscription');
        this.loadInitialData();
    }

    private loadInitialData() {
        this.loaderService.openLoading();
        this.sharedService.fetchStatesAndCities().toPromise().then((statesRes: any) => {
            this.states = (statesRes && statesRes.data && statesRes.data.states) || [];
        }).catch((err) => {
            console.error('Failed to load states or applicable types', err);
            this.toasterService.showToaster(new Toaster({
                type: ToasterType.WARNING,
                message: 'Could not load states.'
            }));
        }).finally(() => this.loaderService.closeLoading());
    }

    private fetchModes() {
        const city = this.formValue("selectedCity");
        if (!city || this.isCashback) {
            return;
        }
        this.loaderService.openLoading();
        this.sharedService.fetchModes(city.id).toPromise()
            .then((res: any) => {
                this.modes = (res && res.data && res.data.modes) || [];
            })
            .catch((err) => {
                console.error('Failed to load modes', err);
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.WARNING,
                    message: 'Could not load modes for city.'
                }));
            })
            .finally(() => this.loaderService.closeLoading());
    }

    private fetchCategoriesAndServices() {
        const city = this.formValue("selectedCity");
        const mode = this.formValue("selectedMode");
        if (!city || !mode || this.isCashback) {
            return;
        }
        this.loaderService.openLoading();
        this.sharedService.fetchSubscriptionServiceOptions(city.id, mode.id).toPromise()
            .then((res: any) => {
                this.categories = (res && res.data && res.data.categories) || [];
                this.allServices = (res && res.data && res.data.services) || [];
            })
            .catch((err) => {
                console.error('Failed to load categories and services', err);
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.WARNING,
                    message: 'Could not load categories for mode.'
                }));
            })
            .finally(() => this.loaderService.closeLoading());
    }

    private filterPrepaidServices() {
        const city = this.formValue("selectedCity");
        const mode = this.formValue("selectedMode");
        const category = this.formValue("selectedCategory");
        if (!city || !mode || !category) {
            this.services = [];
            this.configsBySdid = {};
            return;
        }
        const categoryServices = this.allServices
            .filter(service => String(service.categoryId) === String(category.id));
        const sdids = categoryServices.map(service => service.serviceDetailId).filter(Boolean);
        if (!sdids.length) {
            this.services = [];
            this.configsBySdid = {};
            return;
        }
        this.loaderService.openLoading();
        this.subscriptionService.getApplicableTypes(sdids, mode.id).toPromise()
            .then((res: any) => {
                this.configsBySdid = (res && res.data) || {};
                this.services = categoryServices
                    .filter(service => planTypeFromConfig(this.configsBySdid[service.serviceDetailId]) === 'subscription')
                    .map(service => Object.assign({}, service, { mode: mode.id }));
            })
            .catch((err) => {
                console.error('Failed to load applicable types', err);
                this.services = [];
                this.configsBySdid = {};
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.WARNING,
                    message: 'Could not load applicable types for services.'
                }));
            })
            .finally(() => this.loaderService.closeLoading());
    }

    private matchSelectedService() {
        const service = this.formValue("selectedService");
        if (!service) {
            this.matchedRule = null;
            return;
        }
        const config = this.configsBySdid[service.serviceDetailId];
        this.matchedRule = config ? { applicableType: config } : null;
    }

    private applicableConfig() {
        return applicableConfig(this.matchedRule) || {};
    }

    async createSubscriptionRules() {
        this.loaderService.openLoading();
        try {
          const rules = await this.buildRules();
          if (!rules) {
            return;
          }

          const payload = {
            type: this.formValue("selectedType"),
            rules: rules,
          };

          let resp: any = await this.subscriptionService.createSubscriptionRules(payload).toPromise();
          this.alertDialogService.open(subscriptionCreateSuccessAlert(resp, { configRequestKind: 'subscription' }));
        } catch (error) {
          this.alertDialogService.open(subscriptionOperationFailureAlert(error));
        } finally {
          this.loaderService.closeLoading();
        }
    }

    checkDuplicatePriority(priorityArray){
      const seenPriorities = new Set();
      for (const priority of priorityArray) {
        if (seenPriorities.has(priority)) {
          this.showAlertBox({
            title: "Error",
            message: `Duplicate priority detected: ${priority}`,
          });
          return true;
        }
        seenPriorities.add(priority);
      }
      return false;
    }

    async buildRules() {
        let inputRules = this.fileDetails.records;
        let rules = [];
        for (const rule of inputRules) {
          let ruleName = rule.ruleName;
          let ruleAmount = rule.ruleAmount;
          let priority = rule.priority;
          const isCashbackType = this.formValue("selectedType") === "CASHBACK";
          let userSelectors = isCashbackType ? [] : [rule.userSelectors];
          let userSelectorName = isCashbackType ? null : rule.userSelectorName;
          let periodInDays = rule.periodInDays;
          let excludeIncentiveTypes = rule.excludeIncentiveTypes ? rule.excludeIncentiveTypes.split(",") : [];
          let ruleTitle = rule.ruleTitle;
          let startTime = moment(this.minDate).unix();
          let endTime = moment(this.maxDate).unix();
          let ruleType = isCashbackType ? "qualityReset" : (rule.ruleType || "zeroCommission");
          let ruleDetails = {
            variable: "commission",
            operator: "=",
            value: 0,
            type: "flat"
          };

          if (isCashbackType) {
            rule.validityType = "dapr";
          }

          let city, services, serviceDetails, mode;
          if (isCashbackType) {
            city = rule.cityId;
            services = String(rule.serviceId).split(",").map(s => s.trim());
            serviceDetails = String(rule.serviceDetailId).split(",").map(s => s.trim());
            mode = rule.mode;
          } else {
            city = this.formValue("selectedCity").id;
            services = [this.formValue("selectedService").id];
            serviceDetails = [this.formValue("selectedService").serviceDetailId];
            mode = this.formValue("selectedMode").id;
          }
          let exclusiveOfTax = this.applicableConfig().exclusiveOfTax === true;

          const validValidityTypes: string[] = environment.validityTypesEnabled;
          if (!validValidityTypes.includes(rule.validityType)) {
            this.alertDialogService.open({
              title: "Error",
              message: `Invalid validityType "${rule.validityType}" for rule "${ruleName}". Valid types are: ${validValidityTypes.join(", ")}.`,
            });
            return null;
          }

          let validityObj: any = { type: rule.validityType };
          if (rule.validityMaxValue != null && rule.validityMaxValue !== '') {
            validityObj.maxValue = rule.validityMaxValue;
          }
          if (rule.validityMinValue != null && rule.validityMinValue !== '') {
            validityObj.minValue = rule.validityMinValue;
          }
          let validity = [validityObj];

          let isAutoAssignedToHH = false;
          let isAutoAssignedToMC = false;
          const applicable = this.applicableConfig();
          if (rule.blockOrdersPostExpiry && !isCashbackType && !applicable.alwaysSuspendServicesPostExpiry && !applicable.allowSuspendServicesPostExpiry) {
            return this.alertDialogService.open({
                title: "Error",
                message: "Blocking plan can only be applied when the applicable rule allows block services"
            });
          }
          let suspendServicesPostExpiry = isCashbackType
            || applicable.alwaysSuspendServicesPostExpiry === true
            || (rule.blockOrdersPostExpiry && applicable.allowSuspendServicesPostExpiry === true);

          let ruleConstruct = {
            ruleName,
            ruleAmount,
            priority,
            userSelectors,
            userSelectorName,
            periodInDays,
            excludeIncentiveTypes,
            ruleTitle,
            startTime,
            endTime,
            city,
            services,
            serviceDetails,
            mode,
            exclusiveOfTax,
            ruleType,
            rule: [ruleDetails],
            validity,
            isAutoAssignedToHH,
            isAutoAssignedToMC,
            suspendServicesPostExpiry,
          };
          rules.push(ruleConstruct);
        }
        return rules;
    }

    async checkForOverlappingRules() {
        const isCashback = this.formValue("selectedType") === "CASHBACK";
        let serviceDetailIds: string[];
        if (isCashback) {
            serviceDetailIds = Array.from(new Set<string>(
                this.fileDetails.records.flatMap(r => String(r.serviceDetailId).split(",").map(s => s.trim()))
            ));
        } else {
            serviceDetailIds = [this.formValue("selectedService").serviceDetailId];
        }
        let startTime = moment(this.minDate).unix();
        let endTime = moment(this.maxDate).unix();

        let priorityArray = this.fileDetails.records.map(rule => rule.priority);

        if(this.checkDuplicatePriority(priorityArray)){
          return true;
        }

        try {
          let rules: any = await this.subscriptionService.getSamePriorityRules(serviceDetailIds as [string], startTime, endTime, priorityArray).toPromise();
          if (rules && rules.length > 0) {
            this.overlappingRules = rules
            this.dialog.open(this.OverlappingPriorityRuleAlert);
            return true;
          }
            return false;
        } catch(error) {
          this.alertDialogService.open({
            title: "Error",
            message: JSON.stringify(error.message),
          })
          return true;
        }
    }

    getValues(name: string) {
        return this.subscriptionConstructFormGroup.get(name).value;
    }


    changeInDate() {
        if (this.getValues('fromDate')) {
          this.minDate = this.getValues('fromDate');
        }
        if (this.getValues('toDate')) {
          this.maxDate = this.getValues('toDate');
        }
      }

    canConstructBeUploaded() {
        if (this.isCashback) {
            return true;
        }
        return this.getValues("selectedState")
            && this.getValues("selectedCity")
            && this.getValues("selectedMode")
            && this.getValues("selectedCategory")
            && this.getValues("selectedService");
    }

    resetFile() {
        this.fileDetails = null;
    };

    handleFile(event: { files: File[]; }) {
        const file = event.files[0];
        this.resetFile();
        Papa.parse(file, {
            dynamicTyping: true,
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
            transform: (value) => value.trim(),
            complete: (results) => {
                this.fileDetails = { records: results.data, name: file.name };
            }
        });
    }



    async submitConstructs() {
        this.loaderService.openLoading();
        let overlappingRulesExist = await this.checkForOverlappingRules();
        if (overlappingRulesExist) {
            this.loaderService.closeLoading();
            return;
        }
        this.createSubscriptionRules()
        .finally(() => {
            this.loaderService.closeLoading();
        })
    }

    showAlertBox(options: AlertDialogOptions, cb = () => { }) {
        this.alertDialogService.open(options);
        this.alertDialogService.confirmed()
            .subscribe(confirmed => confirmed && cb());
    }

    formatDate(date) {
        return moment(date).format('YYYY-MM-DD');
    };

    get sampleFileHeaders(): string[] {
        const isCashback = this.formValue("selectedType") === "CASHBACK";
        return isCashback
            ? environment.cashbackSubscriptionRuleCSVHeaders
            : environment.regularSubscriptionRuleCSVHeaders;
    }

    get sampleFileRow(): string[] {
        const isCashback = this.formValue("selectedType") === "CASHBACK";
        const row = isCashback
            ? environment.cashbackSubscriptionRuleSampleRow
            : environment.regularSubscriptionRuleSampleRow;
        return row.map((cell) => this.formatSampleCell(cell));
    }

    formatSampleCell(value: any): string {
        if (Array.isArray(value)) {
            return value.join(",");
        }
        return String(value);
    }

    downloadSampleFile() {
        const isCashback = this.formValue("selectedType") === "CASHBACK";
        Utils.downloadCSVFile({
            fields: isCashback ? environment.cashbackSubscriptionRuleCSVHeaders : environment.regularSubscriptionRuleCSVHeaders,
            data: isCashback ? environment.cashbackSubscriptionRuleSampleRow : environment.regularSubscriptionRuleSampleRow,
            fileName: `subscription_rule_construct_sample.csv`,
        });
    }
}
